import {gql} from '@apollo/client';

import {SchemaPolicy} from 'auth/PolicyConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';

import {SchemaPoliciesQuery, SchemaPoliciesQueryVariables} from './__generated/SchemaPoliciesQuery';

export const SCHEMA_POLICIES_QUERY = gql`
  query SchemaPoliciesQuery($limitOffsetInput: LimitOffsetInput!)
  @skipAudit
  @engComponent(name: PERMISSIONS) {
    schemaPolicyListing(limitOffsetInput: $limitOffsetInput) {
      schemaPolicies {
        id
        name
        permissions {
          writable
        }
        policyStatements {
          itemType
          action
          condition
        }
        isHidden
      }
    }
  }
`;

// We only ever request the first 3 results because because end users are not able to add new schema policies.
// When we lift this restriction, we will need to add a proper paging UI.
export const SCHEMA_POLICIES_QUERY_VARIABLES: SchemaPoliciesQueryVariables = {
  limitOffsetInput: {
    limit: 3,
    offset: 0,
  },
};

export interface SuccessfulSchemaPoliciesListingResult {
  queryResultType: QueryResultType.SUCCESS;
  schemaPolicies: Array<SchemaPolicy & {__typename: 'SchemaPolicyNode'}>;
}
export interface NonSuccessfulSchemaPoliciesListingResult {
  queryResultType: Exclude<QueryResultType, QueryResultType.SUCCESS>;
}
export type SchemaPoliciesListingResult =
  | SuccessfulSchemaPoliciesListingResult
  | NonSuccessfulSchemaPoliciesListingResult;

/**
 * A hook to access a GraphQL query fetching schema policies.
 */
export default function useSchemaPoliciesListing(): SchemaPoliciesListingResult {
  const result = useBenchlingQuery<SchemaPoliciesQuery, SchemaPoliciesQueryVariables>(SCHEMA_POLICIES_QUERY, {
    variables: SCHEMA_POLICIES_QUERY_VARIABLES,
  });

  if (result.type !== QueryResultType.SUCCESS) {
    return {queryResultType: result.type};
  }
  return {queryResultType: result.type, schemaPolicies: result.data.schemaPolicyListing.schemaPolicies};
}
